/**
 * Zitatprüfung — der Schutz gegen erfundene Belege (Plan Stufe 7.1).
 *
 * Die KI muss jede Angabe mit einem wörtlichen Zitat belegen. Ob das Zitat
 * wirklich im Dokument steht, entscheidet nicht die KI, sondern diese Datei:
 * gesucht wird im Text, den der Client selbst aus dem PDF gezogen hat (bzw.
 * im eingefügten Mailtext). Was dort nicht steht, wird verworfen.
 *
 * Verglichen wird NORMALISIERT, weil Textebene und KI-Ausgabe sich in
 * Äußerlichkeiten unterscheiden, die am Inhalt nichts ändern:
 *  - Leerraum (Zeilenumbrüche, geschützte Leerzeichen, Mehrfach-Leerzeichen);
 *    im PDF-Text zählt Leerraum gar nicht, weil pdf.js Wortabstände nur aus
 *    Lücken rät („Baustellen ein richtung", „einKran"),
 *  - Silbentrennung am Zeilenende („Bau-\nstelle" = „Baustelle"),
 *  - typografische Anführungszeichen (fallen weg) und Bindestriche/Gedankenstriche,
 *  - Groß-/Kleinschreibung, Ligaturen und Vollbreitzeichen (NFKC),
 *  - weiches Trennzeichen und Nullbreitenzeichen.
 *
 * Ein Treffer zählt aber nur an WORTGRENZEN im Originaltext: „Ein Kran ist
 * vorgesehen" steht nicht in „Kein Kran ist vorgesehen". Zurück kommt der
 * ganze Satz aus dem DOKUMENT, nie die Abschrift der KI — so steht eine
 * Verneinung, die die KI weggelassen hat, sichtbar im Vorschlag. Steht sie
 * unmittelbar vor oder nach dem Treffer im selben Satzteil, gilt das Zitat
 * als abweichend.
 *
 * Satzende ist ein Punkt nur, wenn davor kein Abkürzungs- oder Zahlwort
 * steht („bzw.", „ggf.", „2. OG") und danach kein Kleinbuchstabe folgt. Ein
 * Seitenwechsel ist KEINE Grenze — ein Satz läuft über die Seite weiter.
 *
 * Rein und ohne pdf.js — prüfbar ohne Worker und DOM.
 */

/**
 * Kürzere Zitate belegen nichts: „Kran ist" steht in fast jeder
 * Baubeschreibung. Gezählt ohne Leerraum; zusätzlich gilt ZITAT_MIN_WOERTER
 * (so verlangt es auch der Prompt).
 */
export const ZITAT_MIN_ZEICHEN = 12;
export const ZITAT_MIN_WOERTER = 5;
/** So weit reicht der Satz um einen Treffer höchstens in jede Richtung. */
const SATZ_MAX_ZEICHEN = 300;
/** So viele Wörter vor und nach dem Treffer werden auf eine weggelassene Verneinung geprüft. */
const VERNEINUNG_FENSTER = 2;

/** Weiches Trennzeichen, Nullbreitenzeichen, Wortverbinder, BOM. */
const UNSICHTBAR = /[\u{00AD}\u{200B}-\u{200D}\u{2060}\u{FEFF}]/u;
/** Gerade und typografische Anführungszeichen, Apostrophe, Akzent, Minutenstriche. */
const ANFUEHRUNG = /["'`\u{00B4}\u{2018}-\u{201F}\u{00AB}\u{00BB}\u{2039}\u{203A}\u{2032}\u{2033}]/u;
/** Bindestriche, Gedankenstriche, Minus (auch Vollbreite). */
const STRICH = /[\u{2010}-\u{2015}\u{2212}\u{FE58}\u{FE63}\u{FF0D}]/u;
const LEERRAUM = /\s/u;
const WORTZEICHEN = /[\p{L}\p{N}]/u;
/** Satzteilzeichen für die Verneinungsprüfung: Komma, Semikolon, Doppelpunkt, Klammer, Gedankenstrich. */
const SATZTEILZEICHEN = /[,;:()[\]\u{2013}\u{2014}]/u;
/** Seitenmarke, wie der Client sie in den Text setzt — gehört nie ins Zitat. */
const SEITENMARKE = /\[\[\s*Seite\s+\d+\s*\]\]/gi;

/**
 * Wörter, die eine Aussage umdrehen. Gebraucht zweimal: eine weggelassene
 * Verneinung neben dem Treffer macht das Zitat abweichend, und ein Nein gilt
 * nur, wenn im belegten Satz eine Verneinung steht.
 */
export const VERNEINUNGEN: ReadonlySet<string> = new Set([
    'kein', 'keine', 'keinen', 'keinem', 'keiner', 'keines', 'keinerlei',
    'nicht', 'nichts', 'ohne', 'nie', 'niemals', 'nirgends', 'weder', 'nein',
    'entfällt', 'entfallen', 'entfiel', 'ausgeschlossen', 'unzulässig', 'untersagt',
    'verboten', 'unnötig', 'verzichtet', 'erübrigt',
]);

/**
 * Wörter vor einem Punkt, nach denen der Satz NICHT endet. Ein einzelner
 * Buchstabe („z. B.", „u. a.") und eine Zahl („2. OG", „17. Okt.") zählen
 * immer als Abkürzung.
 */
const ABKUERZUNGEN: ReadonlySet<string> = new Set([
    'bzw', 'ggf', 'ca', 'gem', 'lt', 'vgl', 'evtl', 'inkl', 'zzgl', 'exkl', 'bzgl', 'nr', 'abs', 'kap', 'pos',
    'usw', 'etc', 'max', 'min', 'mind', 'sog', 'bspw', 'ff', 'tel', 'fa', 'hr', 'fr', 'str', 'std', 'abschn',
    'anl', 'ggü', 'gez', 'od', 'vs', 'insb', 'abb', 'tab', 'ziff', 'lfd', 'bauj', 'stk', 'dipl', 'ing', 'dr', 'prof',
    'mio', 'mrd', 'tsd',
]);

export interface Normalisiert {
    text: string;
    /** Je Zeichen (UTF-16-Einheit) von `text` der Index des Ursprungszeichens im Rohtext. */
    herkunft: number[];
}

/** Normalisiert und merkt sich für jedes Zeichen, woher es stammt. */
export const normalisiereMitHerkunft = (roh: string): Normalisiert => {
    // Schritt 1: Zeichen für Zeichen abbilden — je UTF-16-Einheit eine
    // Herkunft, damit Index im Text und Index in `herkunft` übereinstimmen.
    const zeichen: string[] = [];
    const quelle: number[] = [];
    let i = 0;
    for (const cp of roh) {
        for (const z of cp.normalize('NFKC')) {
            if (UNSICHTBAR.test(z) || ANFUEHRUNG.test(z)) continue;
            const abgebildet = STRICH.test(z) ? '-' : LEERRAUM.test(z) ? ' ' : z.toLowerCase();
            for (let u = 0; u < abgebildet.length; u++) {
                zeichen.push(abgebildet[u]);
                quelle.push(i);
            }
        }
        i += cp.length;
    }

    // Schritt 2: Leerraum zusammenziehen, Trennstrich + Leerraum entfernen.
    // Die Regel gilt auf BEIDEN Seiten des Vergleichs gleich: „Bau-\nstelle"
    // und „Baustelle" werden gleich, ebenso „Sicherheits- und" im Text und im
    // Zitat. Sie gilt nicht nur am Zeilenende, weil die Textebene nicht
    // verrät, ob ein Strich dort Trennung oder Ergänzungsstrich war.
    const text: string[] = [];
    const herkunft: number[] = [];
    for (let k = 0; k < zeichen.length; k++) {
        const z = zeichen[k];
        if (z === ' ') {
            if (text.length > 0 && text[text.length - 1] !== ' ') {
                text.push(' ');
                herkunft.push(quelle[k]);
            }
            continue;
        }
        if (z === '-') {
            let j = k + 1;
            while (j < zeichen.length && zeichen[j] === ' ') j++;
            if (j > k + 1 && j < zeichen.length) {
                k = j - 1;
                continue;
            }
        }
        text.push(z);
        herkunft.push(quelle[k]);
    }
    if (text[text.length - 1] === ' ') {
        text.pop();
        herkunft.pop();
    }
    return { text: text.join(''), herkunft };
};

export const normalisiere = (roh: string): string => normalisiereMitHerkunft(roh).text;

const woerterIn = (normalisiert: string): number =>
    normalisiert.split(' ').filter((w) => WORTZEICHEN.test(w)).length;

/** Lang genug, um als Beleg zu taugen? Mindestens 12 Zeichen und 5 Wörter. */
export const zitatPruefbar = (zitat: string): boolean => {
    const n = normalisiere(zitat.replace(SEITENMARKE, ' '));
    return n.replace(/ /g, '').length >= ZITAT_MIN_ZEICHEN && woerterIn(n) >= ZITAT_MIN_WOERTER;
};

/** Steht im Text irgendein Wort aus VERNEINUNGEN? */
export const enthaeltVerneinung = (text: string): boolean =>
    normalisiere(text).split(/[^\p{L}]+/u).some((w) => VERNEINUNGEN.has(w));

// ─── Satz und Satzteil am Originaltext ────────────────────────────────────

const laengeBei = (text: string, index: number): number => ((text.codePointAt(index) ?? 0) > 0xffff ? 2 : 1);

/** Beginnt bei `von` ein Wort? (unsichtbare Zeichen davor zählen nicht) */
const grenzeVor = (text: string, von: number): boolean => {
    let p = von - 1;
    while (p >= 0 && UNSICHTBAR.test(text[p])) p--;
    return p < 0 || !WORTZEICHEN.test(text[p]);
};

/** Endet bei `bis` (exklusiv) ein Wort? */
const grenzeNach = (text: string, bis: number): boolean => {
    let q = bis;
    while (q < text.length && UNSICHTBAR.test(text[q])) q++;
    return q >= text.length || !WORTZEICHEN.test(text[q]);
};

/** Ist das Wort (mit oder ohne Schlusspunkt) eine Abkürzung oder Zahl, nach der kein Satz endet? */
const istAbkuerzung = (wort: string): boolean => {
    const w = wort.replace(/\.$/, '').toLowerCase();
    return w.length === 1 || /^\d+$/.test(w) || ABKUERZUNGEN.has(w);
};

/**
 * Endet bei `p` ein Satz? „!" und „?" vor Leerraum immer. Ein Punkt nur vor
 * Leerraum (oder Textende), wenn danach kein Kleinbuchstabe folgt und davor
 * kein Abkürzungs- oder Zahlwort steht.
 */
export const istSatzende = (text: string, p: number): boolean => {
    const c = text[p];
    if (c !== '.' && c !== '!' && c !== '?') return false;
    const q = p + 1;
    if (q < text.length && !LEERRAUM.test(text[q])) return false;
    if (c !== '.') return true;
    let r = q;
    while (r < text.length && LEERRAUM.test(text[r])) r++;
    if (r < text.length && /\p{Ll}/u.test(text[r])) return false;
    let a = p - 1;
    while (a >= 0 && WORTZEICHEN.test(text[a])) a--;
    const wort = text.slice(a + 1, p);
    return !wort || !istAbkuerzung(wort);
};

/** Ein Mailgruß in eigener Zeile („Hallo Herr Muster,") — danach beginnt ein neuer Satz. */
const GRUSSZEILE = /^\s*(?:hallo|guten\s+(?:tag|morgen|abend)|sehr\s+geehrte|liebe|lieber|moin|servus|hi)\b.*,\s*$/i;
/** Eine Mailzeile, die mit einem Fragecode beginnt („Zu PL-E-01: …") — dort beginnt die nächste Antwort. */
const FRAGECODE_ZEILE = /^\s*(?:zu\s+)?[a-z]{1,5}-[a-z0-9]{1,5}-\d{1,4}\b/i;
/** Listenzeile in der Mail („1. …", „2) …", „- …", „• …") — jeder Punkt ist eine eigene Aussage. */
const LISTENZEILE = /^\s*(?:\d{1,2}[.)]|[-\u{2022}*])\s/u;
/** Schlussgruß in eigener Zeile — danach kommt keine Antwort mehr. */
const SCHLUSSGRUSS = /^\s*(?:(?:viele|beste|freundliche|liebe|herzliche)\s+grüße|mit\s+freundlichen\s+grüßen|gruß|grüße|mfg|lg|vg|(?:best|kind)\s+regards|regards|cheers)[\s,.!]*$/i;

const zeileUm = (text: string, p: number): { vorher: string; nachher: string } => {
    const anfangVorher = text.lastIndexOf('\n', p - 1) + 1;
    const endeNachher = text.indexOf('\n', p + 1);
    return { vorher: text.slice(anfangVorher, p), nachher: text.slice(p + 1, endeNachher < 0 ? text.length : endeNachher) };
};

/**
 * Ist der Zeilenumbruch bei `p` eine Satzgrenze? Im Dokument nur an einer
 * Leerzeile; in der Mail zusätzlich an zitierten Zeilen („> …") und nach
 * einer Grußzeile. Ein Seitenwechsel ist ein einfacher Umbruch — keine Grenze.
 */
const istZeilengrenze = (text: string, p: number, mail: boolean): boolean => {
    const { vorher, nachher } = zeileUm(text, p);
    if (!vorher.trim() || !nachher.trim()) return true;
    if (!mail) return false;
    return /^\s*>/.test(vorher) || /^\s*>/.test(nachher)
        || GRUSSZEILE.test(vorher) || SCHLUSSGRUSS.test(vorher) || SCHLUSSGRUSS.test(nachher)
        || FRAGECODE_ZEILE.test(nachher) || LISTENZEILE.test(nachher);
};

interface SatzOptionen {
    /** Harte Grenzen (z. B. Ende des eigenen Mailtexts). */
    untergrenze?: number;
    obergrenze?: number;
    mail?: boolean;
}

/**
 * Erweitert [von, bis) auf den ganzen Satz, höchstens SATZ_MAX_ZEICHEN je
 * Richtung (dann an einer Wortgrenze gekappt).
 */
const satzGrenzen = (text: string, von: number, bis: number, opt: SatzOptionen = {}): { anfang: number; ende: number } => {
    const hartUnten = opt.untergrenze ?? 0;
    const hartOben = Math.min(opt.obergrenze ?? text.length, text.length);
    const mail = !!opt.mail;

    const unten = Math.max(hartUnten, von - SATZ_MAX_ZEICHEN);
    let anfang = unten;
    let gefunden = unten === hartUnten;
    for (let p = von - 1; p >= unten; p--) {
        if (istSatzende(text, p) || (text[p] === '\n' && istZeilengrenze(text, p, mail))) {
            anfang = p + 1;
            gefunden = true;
            break;
        }
    }
    if (!gefunden) {
        while (anfang < von && !LEERRAUM.test(text[anfang - 1] ?? ' ')) anfang++;
    }

    const oben = Math.min(hartOben, bis + SATZ_MAX_ZEICHEN);
    let ende = oben;
    gefunden = oben === hartOben;
    if (bis > 0 && istSatzende(text, bis - 1)) {
        ende = bis;
        gefunden = true;
    } else {
        for (let q = bis; q < oben; q++) {
            if (istSatzende(text, q)) { ende = q + 1; gefunden = true; break; }
            if (text[q] === '\n' && istZeilengrenze(text, q, mail)) { ende = q; gefunden = true; break; }
        }
    }
    if (!gefunden) {
        while (ende > bis && !LEERRAUM.test(text[ende] ?? ' ')) ende--;
    }
    return { anfang, ende };
};

/** Der Satz um einen Treffer im Dokument, lesbar: Leerraum zusammengezogen, Trennung am Zeilenende sichtbar. */
const dokumentSatz = (text: string, von: number, bis: number): string => {
    const { anfang, ende } = satzGrenzen(text, von, bis);
    return text
        .slice(anfang, ende)
        // Silbentrennung am Zeilenende lesbar halten: „Turm-\ndrehkran" → „Turm-drehkran".
        .replace(/-[ \t]*\n\s*(?=\p{Ll})/gu, '-')
        .replace(/\s+/g, ' ')
        .trim();
};

/** Bereich des eigenen Mailtexts: nach dem Kopfblock, vor dem zitierten Verlauf. */
export interface TextBereich {
    anfang?: number;
    ende?: number;
}

/**
 * Der Satz um einen Treffer in der Mail, wörtlich (Zeilenumbrüche bleiben),
 * nur im eigenen Text (`bereich`: nie Kopfzeilen wie Von/An/Betreff, nie der
 * zitierte Verlauf) und nie in zitierte Zeilen („> …").
 */
export const mailSatz = (mail: string, von: number, bis: number, bereich: TextBereich = {}): string => {
    const { anfang, ende } = satzGrenzen(mail, von, bis, { untergrenze: bereich.anfang, obergrenze: bereich.ende, mail: true });
    return mail.slice(anfang, ende).trim();
};

/**
 * Bis zu `anzahl` Wörter vor `von` (richtung -1) bzw. ab `bis` (richtung +1)
 * im selben Satzteil, nie über den `bereich` hinaus. Abkürzungen („ggf.",
 * „bzw.") zählen nicht mit.
 */
const nachbarWoerter = (
    text: string,
    start: number,
    richtung: -1 | 1,
    anzahl: number,
    mail: boolean,
    bereich: TextBereich,
): string[] => {
    const woerter: string[] = [];
    let wort = '';
    const nimm = () => {
        if (wort && WORTZEICHEN.test(wort) && !istAbkuerzung(wort)) woerter.push(wort);
        wort = '';
    };
    const grenze = richtung < 0
        ? Math.max(bereich.anfang ?? 0, start - 160)
        : Math.min(bereich.ende ?? text.length, text.length, start + 160);
    for (let p = richtung < 0 ? start - 1 : start; richtung < 0 ? p >= grenze : p < grenze; p += richtung) {
        if (woerter.length >= anzahl) break;
        const c = text[p];
        if (SATZTEILZEICHEN.test(c) || istSatzende(text, p) || (c === '\n' && istZeilengrenze(text, p, mail))) break;
        if (LEERRAUM.test(c)) {
            nimm();
            continue;
        }
        wort = richtung < 0 ? c + wort : wort + c;
    }
    nimm();
    return woerter.slice(0, anzahl);
};

/**
 * Steht im selben Satzteil unmittelbar vor `von` oder nach `bis` (je bis zu
 * zwei Wörter) eine Verneinung? Ein Komma o. Ä. beendet den Blick: in „kein
 * Turmdrehkran, sondern ein Mobilkran" gehört das „kein" nicht zum Mobilkran.
 */
export const verneinungAmRand = (
    text: string,
    von: number,
    bis: number,
    mail: boolean = false,
    bereich: TextBereich = {},
): boolean => {
    const istVerneinung = (w: string) => VERNEINUNGEN.has(normalisiere(w).replace(/[^\p{L}]/gu, ''));
    if (nachbarWoerter(text, von, -1, VERNEINUNG_FENSTER, mail, bereich).some(istVerneinung)) return true;
    // Endet der Treffer selbst an einer Satzteil- oder Satzgrenze, gehört das Folgende nicht mehr dazu.
    if (bis > 0 && (SATZTEILZEICHEN.test(text[bis - 1]) || istSatzende(text, bis - 1))) return false;
    return nachbarWoerter(text, bis, 1, VERNEINUNG_FENSTER, mail, bereich).some(istVerneinung);
};

// ─── PDF-Zitate ───────────────────────────────────────────────────────────

export type ZitatPruefung =
    | {
        ok: true;
        seite: number;
        /** Der ganze Satz aus dem DOKUMENT, in dem das Zitat steht. */
        dokumentZitat: string;
        /** Genau die Stelle im Dokument, die dem Zitat entspricht (Originalzeichen). */
        treffer: string;
    }
    | {
        ok: false;
        /** `abweichung`: gefunden, aber unmittelbar daneben steht eine Verneinung, die im Zitat fehlt. */
        grund: 'zu_kurz' | 'nicht_gefunden' | 'abweichung';
    };

/**
 * Bereitet die Seiten EINMAL für viele Zitate vor (ein Lauf prüft bis zu
 * 60 Merkmale gegen dasselbe Dokument). Seitenübergreifende Zitate werden
 * gefunden und der Seite zugeordnet, auf der sie beginnen.
 */
export const erstelleZitatPruefung = (
    seiten: { seite: number; text: string }[],
): ((zitat: string) => ZitatPruefung) => {
    // Originaltext aller Seiten. Verbunden mit EINEM Umbruch: ein Satz läuft
    // über den Seitenwechsel weiter, und eine Verneinung auf der nächsten
    // Seite bleibt Nachbar des Treffers.
    let roh = '';
    const anfaenge: { ab: number; seite: number }[] = [];
    for (const s of seiten) {
        if (!s.text.trim()) continue;
        if (roh) roh += '\n';
        anfaenge.push({ ab: roh.length, seite: s.seite });
        roh += s.text;
    }
    // Normalisiert und ohne Leerraum, mit Herkunft je Zeichen.
    const norm = normalisiereMitHerkunft(roh);
    const kompaktZeichen: string[] = [];
    const herkunft: number[] = [];
    for (let k = 0; k < norm.text.length; k++) {
        if (norm.text[k] === ' ') continue;
        kompaktZeichen.push(norm.text[k]);
        herkunft.push(norm.herkunft[k]);
    }
    const kompakt = kompaktZeichen.join('');

    const seiteBei = (index: number): number => {
        let seite = anfaenge[0]?.seite ?? 1;
        for (const a of anfaenge) {
            if (a.ab > index) break;
            seite = a.seite;
        }
        return seite;
    };

    return (zitat: string): ZitatPruefung => {
        if (!zitatPruefbar(zitat)) return { ok: false, grund: 'zu_kurz' };
        const nadel = normalisiere(zitat.replace(SEITENMARKE, ' ')).replace(/ /g, '');
        const wortAnfang = WORTZEICHEN.test(nadel[0]);
        const wortEnde = WORTZEICHEN.test(nadel[nadel.length - 1]);
        let abweichung = false;
        for (let stelle = kompakt.indexOf(nadel); stelle >= 0; stelle = kompakt.indexOf(nadel, stelle + 1)) {
            const von = herkunft[stelle];
            const letztes = herkunft[stelle + nadel.length - 1];
            const bis = letztes + laengeBei(roh, letztes);
            if ((wortAnfang && !grenzeVor(roh, von)) || (wortEnde && !grenzeNach(roh, bis))) continue;
            if (verneinungAmRand(roh, von, bis)) {
                abweichung = true;
                continue;
            }
            return { ok: true, seite: seiteBei(von), dokumentZitat: dokumentSatz(roh, von, bis), treffer: roh.slice(von, bis) };
        }
        return { ok: false, grund: abweichung ? 'abweichung' : 'nicht_gefunden' };
    };
};

/**
 * Steht das Zitat (normalisiert, an Wortgrenzen, ohne weggelassene
 * Verneinung) in den Seiten? Liefert die Seite, auf der es beginnt — oder
 * `null`, auch wenn es zu kurz ist (`zitatPruefbar`).
 */
export const findeZitat = (
    zitat: string,
    seiten: { seite: number; text: string }[],
): { seite: number } | null => {
    const p = erstelleZitatPruefung(seiten)(zitat);
    return 'grund' in p ? null : { seite: p.seite };
};

// ─── Zahlen ───────────────────────────────────────────────────────────────

/**
 * Zahlwörter (Ziffern) im Text: „1.500", „1500", „5,5", „1.500,5", „ca.25"
 * und „1 500" NUR mit geschütztem oder schmalem Leerzeichen als
 * Tausendertrenner — ein normales Leerzeichen trennt zwei Zahlen
 * („Bauabschnitt 2 500 Personen" sind 2 und 500, nicht 2500). Nicht aus
 * Bezeichnern („A2.1", „DN100") und nicht aus Datum oder Positionsnummer
 * („12.03.2026", „01.02.0030" ergeben keine Zahl).
 */
const ZAHL = /(?<![\p{L}\p{N}])(?<!\p{N}[.,])(?:\d{1,3}(?:[\u{00A0}\u{2009}\u{202F}]\d{3}(?!\d))+|\d+(?:[.,]\d+)*)/gu;

const leseZahl = (token: string): number => {
    const t = token.replace(/[\u{00A0}\u{2009}\u{202F}]/gu, '');
    if (t.includes(',')) {
        const teile = t.split(',');
        if (teile.length !== 2 || !/^\d{1,3}(?:\.\d{3})*$|^\d+$/.test(teile[0])) return NaN;
        return Number(`${teile[0].replace(/\./g, '')}.${teile[1]}`);
    }
    if (/^\d{1,3}(?:\.\d{3})+$/.test(t)) return Number(t.replace(/\./g, ''));
    return Number(t);
};

export const zahlenIn = (text: string): number[] =>
    [...text.matchAll(ZAHL)].map((m) => leseZahl(m[0])).filter((n) => Number.isFinite(n));

/** Steht `wert` als eigenes Zahlwort im Text? */
export const zahlSteht = (wert: number, text: string): boolean =>
    zahlenIn(text).some((z) => Math.abs(z - wert) < 1e-9);

// ─── Mail ─────────────────────────────────────────────────────────────────

/**
 * Sucht einen Ausschnitt normalisiert in `text` und gibt ihn so zurück, wie
 * er DORT steht (Originalzeichen, nur außen getrimmt), samt Stelle — oder
 * `null`. Der Treffer muss an Wortgrenzen beginnen und enden, sonst stünde
 * „ja" schon in „Jahr". `zulassen` filtert Fundstellen (z. B. zitierte
 * Zeilen einer Mail); gesucht wird die erste zugelassene.
 */
export const findeWoertlichStelle = (
    ausschnitt: string,
    text: string,
    opt: { minZeichen?: number; zulassen?: (von: number, bis: number) => boolean } = {},
): { von: number; bis: number; text: string } | null => {
    const nadel = normalisiere(ausschnitt);
    if (!nadel || nadel.length < (opt.minZeichen ?? 2)) return null;
    const heu = normalisiereMitHerkunft(text);
    for (let stelle = heu.text.indexOf(nadel); stelle >= 0; stelle = heu.text.indexOf(nadel, stelle + 1)) {
        const ende = stelle + nadel.length;
        const davor = stelle > 0 ? heu.text[stelle - 1] : ' ';
        const danach = ende < heu.text.length ? heu.text[ende] : ' ';
        const amAnfangGrenze = !WORTZEICHEN.test(nadel[0]) || !WORTZEICHEN.test(davor);
        const amEndeGrenze = !WORTZEICHEN.test(nadel[nadel.length - 1]) || !WORTZEICHEN.test(danach);
        if (!amAnfangGrenze || !amEndeGrenze) continue;
        const von = heu.herkunft[stelle];
        const letztes = heu.herkunft[ende - 1];
        const bis = letztes + laengeBei(text, letztes);
        if (opt.zulassen && !opt.zulassen(von, bis)) continue;
        return { von, bis, text: text.slice(von, bis).trim() };
    }
    return null;
};

/** Wie `findeWoertlichStelle`, nur der Ausschnitt. */
export const findeWoertlich = (ausschnitt: string, text: string, minZeichen: number = 2): string | null =>
    findeWoertlichStelle(ausschnitt, text, { minZeichen })?.text ?? null;
